import { css } from '@emotion/react'
import { Icon, PagedTable, Tooltip } from 'bold-ui'
import { usePagedTableProps } from 'components/table'
import { VisualizarPaginaButton } from 'components/VisualizarPaginaButton'
import { useAcompanhamentoPuerperioExamesQuery } from 'graphql/hooks.generated'
import { ExamesPuerperioDto, PageParams, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { Fragment, useState } from 'react'
import { formatDate } from 'util/date/formatDate'

import { AcompanhamentoCard } from '../../components/AcompanhamentoCard'
import { DetalhamentoExameModal } from '../../pre-natal/components/DetalhamentoExameModal'

interface ExamesPuerperioCardProps {
  prontuarioId: ID
  dataInicioPuerperio: Date
  loading?: boolean
}

export function ExamesPuerperioCard(props: Readonly<ExamesPuerperioCardProps>) {
  const { prontuarioId, dataInicioPuerperio, loading } = props

  const [exameSelected, setExameSelected] = useState<ExamesPuerperioDto>()
  const [openModal, setOpenModal] = useState(false)
  const [tableState, setTableState] = useState<{ pageParams: PageParams }>({
    pageParams: {
      size: 8,
      fetchPageInfo: true,
    },
  })

  const { data, loading: loadingExames } = useAcompanhamentoPuerperioExamesQuery({
    variables: { input: { prontuarioId, dataInicioCondicao: dataInicioPuerperio, pageParams: tableState.pageParams } },
    skip: !prontuarioId || !dataInicioPuerperio || loading,
  })

  const hasExames = data?.acompanhamentoPuerperioExames?.content?.length > 0

  const tableProps = usePagedTableProps({
    result: data?.acompanhamentoPuerperioExames,
    loading: loading || loadingExames,
    onChange: setTableState,
  })

  const renderVisualizarInformacoes = (exame: ExamesPuerperioDto) => {
    const handleOnClick = () => {
      setExameSelected(exame)
      setOpenModal(true)
    }

    return <VisualizarPaginaButton tooltipText='Visualizar informações' onClick={handleOnClick} />
  }

  return (
    <Fragment>
      <AcompanhamentoCard
        id='exames-puerperio'
        title='Exames realizados no puerpério'
        tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
        headerContent={
          <Tooltip text='Os dados são aqueles que foram incluídos na parte “Resultados de exames” do SOAP do atendimento'>
            <Icon icon='infoCircleFilled' size={1} />
          </Tooltip>
        }
        bodyPadding={hasExames ? '0' : undefined}
        loading={loading || loadingExames}
      >
        <PagedTable
          columns={[
            {
              header: 'Nome',
              name: 'nomeProcedimento',
              sortable: true,
              render: (exame: ExamesPuerperioDto) =>
                `${exame.nomeProcedimento.capitalize()} - ${exame.codigoProcedimento}`,
            },
            {
              header: 'Data da última realização',
              name: 'ultimaDataRealizacao',
              sortable: true,
              render: (exame: ExamesPuerperioDto) => formatDate(exame.ultimaDataRealizacao),
            },
            {
              header: 'Registros',
              name: 'registros',
              render: (exame: ExamesPuerperioDto) => exame.registros,
            },
            {
              name: 'visualizarInformacoes',
              header: '',
              style: styles.lupa,
              render: renderVisualizarInformacoes,
            },
          ]}
          {...tableProps}
          sizeOptions={[8]}
        />
      </AcompanhamentoCard>
      {openModal && (
        <DetalhamentoExameModal
          prontuarioId={prontuarioId}
          nomeProcedimento={exameSelected.nomeProcedimento}
          codigoProcedimento={exameSelected.codigoProcedimento}
          procedimentoId={exameSelected.procedimentoId}
          dataInicioCondicao={dataInicioPuerperio}
          tipoAcompanhamento={TipoAcompanhamentoEnum.PUERPERIO}
          onClose={() => setOpenModal(false)}
        />
      )}
    </Fragment>
  )
}

const styles = {
  lupa: css`
    padding: 0;
  `,
}
